import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { authAPI } from '../services/api';

export default function LoginPage() {
  const { loginDirect } = useAuth();
  const { theme }       = useTheme();
  const navigate        = useNavigate();

  const [step, setStep]         = useState('login'); // 'login' | 'mfa' | 'mfa-setup'
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode]   = useState('');
  const [qrCode, setQrCode]     = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await authAPI.login(username, password);
      if (res.data.requires_mfa) {
        setStep('mfa');
      } else if (res.data.mfa_setup_required) {
        // MFA obbligatorio per questo utente (nuovo account) ma non ancora
        // configurato. Si salva temporaneamente il token limitato così le
        // chiamate a mfa/setup e mfa/enable lo usano automaticamente
        // (l'interceptor axios legge sempre da qui), senza dare però accesso
        // alle altre pagine: get_current_user lo rifiuta esplicitamente.
        localStorage.setItem('auth_token', res.data.setup_token);
        const qr = await authAPI.mfaSetup();
        setQrCode(qr.data.qr_code);
        setStep('mfa-setup');
      } else {
        loginDirect(res.data.token, res.data.user);
        navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Credenziali non valide');
    } finally { setLoading(false); }
  };

  const handleMFA = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await authAPI.verifyMFA(username, mfaCode);
      loginDirect(res.data.token, res.data.user);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Codice MFA non valido');
    } finally { setLoading(false); }
  };

  const handleMFASetupConfirm = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await authAPI.mfaEnable(username, mfaCode);
      // mfa/enable restituisce ora il token completo: il setup è terminato
      // e l'utente può accedere normalmente al resto del sistema.
      loginDirect(res.data.token, res.data.user);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Codice MFA non valido');
    } finally { setLoading(false); }
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>
        {/* Logo / Nome azienda */}
        <div style={styles.logoArea}>
          {theme.logoUrl
            ? <img src={theme.logoUrl} alt="logo" style={styles.logo} />
            : <div style={{ ...styles.logoPlaceholder, background: theme.primaryColor }}>
                {theme.companyName.charAt(0)}
              </div>
          }
          <h1 style={styles.companyName}>{theme.companyName}</h1>
          <p style={styles.subtitle}>Backup MOC Portal</p>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {step === 'login' ? (
          <form onSubmit={handleLogin} style={styles.form}>
            <label style={styles.label}>Username</label>
            <input
              style={styles.input}
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              autoFocus required
              placeholder="username"
            />
            <label style={styles.label}>Password</label>
            <input
              style={styles.input}
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              placeholder="••••••••"
            />
            <button
              type="submit"
              disabled={loading}
              style={{ ...styles.btn, background: theme.primaryColor }}
            >
              {loading ? 'Accesso in corso...' : 'Accedi'}
            </button>
          </form>
        ) : step === 'mfa' ? (
          <form onSubmit={handleMFA} style={styles.form}>
            <p style={styles.mfaHint}>
              Inserisci il codice a 6 cifre dall'app Authenticator
            </p>
            <label style={styles.label}>Codice MFA</label>
            <input
              style={{ ...styles.input, letterSpacing: '0.3em', fontSize: 22, textAlign: 'center' }}
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={mfaCode}
              onChange={e => setMfaCode(e.target.value.replace(/\D/g, ''))}
              autoFocus required
              placeholder="000000"
            />
            <button
              type="submit"
              disabled={loading || mfaCode.length !== 6}
              style={{ ...styles.btn, background: theme.primaryColor }}
            >
              {loading ? 'Verifica...' : 'Verifica codice'}
            </button>
            <button
              type="button"
              onClick={() => { setStep('login'); setMfaCode(''); setError(''); }}
              style={styles.btnBack}
            >
              ← Torna al login
            </button>
          </form>
        ) : (
          <form onSubmit={handleMFASetupConfirm} style={styles.form}>
            <p style={styles.mfaHint}>
              Per il tuo account è richiesta l'autenticazione a due fattori.
              Scansiona il codice QR con Google Authenticator o Authy, poi
              inserisci il codice generato per completare la configurazione.
            </p>
            {qrCode && (
              <div style={{ textAlign: 'center', margin: '8px 0 16px' }}>
                <img src={qrCode} alt="QR MFA" style={{ width: 180, height: 180 }} />
              </div>
            )}
            <label style={styles.label}>Codice di conferma</label>
            <input
              style={{ ...styles.input, letterSpacing: '0.3em', fontSize: 22, textAlign: 'center' }}
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={mfaCode}
              onChange={e => setMfaCode(e.target.value.replace(/\D/g, ''))}
              autoFocus required
              placeholder="000000"
            />
            <button
              type="submit"
              disabled={loading || mfaCode.length !== 6}
              style={{ ...styles.btn, background: theme.primaryColor }}
            >
              {loading ? 'Attivazione...' : 'Attiva e continua'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const styles = {
  wrapper: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f1f5f9',
    fontFamily: 'Inter, system-ui, sans-serif',
  },
  card: {
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 4px 32px rgba(0,0,0,0.10)',
    padding: '48px 40px',
    width: '100%',
    maxWidth: 400,
  },
  logoArea: { textAlign: 'center', marginBottom: 32 },
  logo: { height: 64, objectFit: 'contain', marginBottom: 12 },
  logoPlaceholder: {
    width: 64, height: 64, borderRadius: 16,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 28, fontWeight: 700, color: '#fff', marginBottom: 12,
  },
  companyName: { margin: '8px 0 4px', fontSize: 22, fontWeight: 700, color: '#1e293b' },
  subtitle:    { margin: 0, color: '#64748b', fontSize: 14 },
  error: {
    background: '#fef2f2', border: '1px solid #fecaca',
    color: '#dc2626', borderRadius: 8, padding: '10px 14px',
    marginBottom: 16, fontSize: 14,
  },
  form:  { display: 'flex', flexDirection: 'column', gap: 12 },
  label: { fontSize: 13, fontWeight: 600, color: '#374151' },
  input: {
    padding: '10px 14px', borderRadius: 8,
    border: '1.5px solid #d1d5db', fontSize: 15,
    outline: 'none', transition: 'border 0.2s',
  },
  btn: {
    marginTop: 8, padding: '12px', borderRadius: 8,
    border: 'none', color: '#fff', fontWeight: 700,
    fontSize: 15, cursor: 'pointer',
  },
  btnBack: {
    background: 'none', border: 'none', color: '#64748b',
    fontSize: 13, cursor: 'pointer', textAlign: 'center',
  },
  mfaHint: { color: '#64748b', fontSize: 14, textAlign: 'center', margin: '0 0 8px' },
};
